import {
	$clearClientsListRestoreIntent,
	$clientsListRestoreIntent,
	type ClientsListType,
} from '@/entities/clients-filter.entity';
import { resolveClientsListRestoreIndex } from '@/lib/clients-list-restore-index';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const SCROLL_SAVE_DEBOUNCE_MS = 100;

export function useVirtualListScrollRestore(options: {
	listType: ClientsListType;
	listRef: RefObject<HTMLDivElement | null>;
	virtualizer: Virtualizer<HTMLDivElement, Element>;
	items: readonly unknown[];
	savedScrollTop: number;
	setScrollTop: (value: number) => void;
	ready: boolean;
}) {
	const { listType, listRef, virtualizer, items, savedScrollTop, setScrollTop, ready } = options;
	const [restoreIntent, clearRestoreIntent] = useUnit([$clientsListRestoreIntent, $clearClientsListRestoreIntent]);
	const restoredRef = useRef(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const el = listRef.current;
		if (!el) return;

		function onScroll() {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				if (listRef.current) setScrollTop(listRef.current.scrollTop);
			}, SCROLL_SAVE_DEBOUNCE_MS);
		}

		el.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			el.removeEventListener('scroll', onScroll);
			if (debounceRef.current) clearTimeout(debounceRef.current);
			if (listRef.current) setScrollTop(listRef.current.scrollTop);
		};
	}, [listRef, setScrollTop]);

	useLayoutEffect(() => {
		if (!ready || items.length === 0 || restoredRef.current) return;

		const runRestore = () => {
			if (!listRef.current || restoredRef.current) return;

			const intent = restoreIntent?.listType === listType ? restoreIntent : null;
			if (intent) {
				const index = resolveClientsListRestoreIndex(intent, items);
				if (index !== null) {
					virtualizer.scrollToIndex(index, { align: 'center' });
					restoredRef.current = true;
					clearRestoreIntent();
					return;
				}
			}

			if (savedScrollTop > 0) {
				virtualizer.scrollToOffset(savedScrollTop);
				restoredRef.current = true;
				if (intent) clearRestoreIntent();
				return;
			}

			if (!restoredRef.current) {
				restoredRef.current = true;
				if (intent) clearRestoreIntent();
			}
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(runRestore);
		});
	}, [
		ready,
		items.length,
		savedScrollTop,
		restoreIntent,
		listType,
		virtualizer,
		listRef,
		clearRestoreIntent,
		items,
	]);
}
